import styled from 'styled-components';

import { ANIM, HOME, ACQ_MOBILE, SPACE } from '../../../../theme/tokens';
import { lightCardShadow } from '../../../../theme/mixins';
import { GlowCard, Text } from '../../../../components';

/**
 * One "Business Optimization Score" gauge card: a GlowCard (the card's base, hairline, radius, glow and hover wash),
 * laid out as a column around the ring.
 */
export const Card = styled(GlowCard)`
  padding: ${SPACE[20]};
  display: flex;
  flex-direction: column;
  align-items: center;

  /* Light theme: the cards' subtle shadow, fading away while hovered (the user, 2026-10-21). */
  ${lightCardShadow}

  :root[data-ows-theme='light'] & {
    transition: box-shadow ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  }

  :root[data-ows-theme='light'] &:hover {
    box-shadow:
      0 1px 2px transparent,
      0 12px 28px -12px transparent;
  }

  ${ACQ_MOBILE} {
    /* 6px a side is off the scale: kept as the phone card had it, so Home does not move. */
    padding: ${SPACE[16]} 6px ${SPACE[12]};
  }
`;

export const Ring = styled.div`
  position: relative;
  width: 148px;
  height: 148px;
  flex-shrink: 0;
  /* Boost the ring arc, faint track and status word by 50% saturation (the score
   * number and labels are near-white, so they read unchanged). Scoped here so the
   * shared gaugeGradeColor stays intact for every other gauge in the app. */
  filter: saturate(1.5);

  /* Light theme: the ring's colours as they are, no filter (the user, 2026-10-21). */
  :root[data-ows-theme='light'] & {
    filter: none;
  }

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  ${ACQ_MOBILE} {
    width: 74px;
    height: 74px;
  }
`;

export const Center = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[4]};
`;

export const Val = styled.div`
  font-size: 48px;
  font-weight: 500;
  line-height: 1;
  color: ${HOME.t50};
  font-variant-numeric: tabular-nums;

  ${ACQ_MOBILE} {
    font-size: 23px;
  }
`;

export const Status = styled(Text)<{ $color: string }>`
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  color: ${(p) => p.$color};

  ${ACQ_MOBILE} {
    font-size: 9px;
  }
`;

export const Label = styled(Text)`
  /* Off the scale: kept as the card had it, so Home does not move. */
  margin-top: 14px;
  font-size: 16px;
  font-weight: 500;
  color: ${HOME.gaugeLabel};

  ${ACQ_MOBILE} {
    font-size: 12px;
    /* Off the scale: kept as the phone card had it. */
    margin-top: 11px;
  }
`;

export const Foot = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  /* Off the scale: kept as the card had it, so Home does not move. */
  margin-top: 14px;

  ${ACQ_MOBILE} {
    justify-content: center;
    /* Off the scale: kept as the phone card had it. */
    margin-top: 10px;
  }
`;

export const Chev = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: ${HOME.t50};
  transition: transform ${ANIM.INTERACT_MS}ms ${HOME.ease};

  ${Card}:hover & {
    transform: translateX(2px);
  }
`;
